/**
 * Events — one naming convention for everything components say to each other.
 *   component events   `<identifier>:<past-tense>`, bubbling from the element (`data-table:loaded`)
 *   app-wide events    `ls:<name>` on window (`ls:toast`, `ls:refresh`, `ls:session-expired`)
 * Details carry ids, counters and enum values only: never PII, never HTML.
 */
const IDENTIFIER = /^[a-z][a-z0-9]*(--?[a-z0-9]+)*$/
const NAME = /^[a-z][a-z0-9]*(-[a-z0-9]+)*$/

/**
 * @param {string} identifier controller identifier (or the reserved `ls`)
 * @param {string} name
 */
export function eventType(identifier, name) {
  if (!IDENTIFIER.test(identifier) || !NAME.test(name)) {
    throw new TypeError(`events: "${identifier}:${name}" is not <identifier>:<event> in kebab-case`)
  }
  return `${identifier}:${name}`
}

/**
 * Dispatch a bubbling CustomEvent. `detail` is a frozen shallow copy: listeners read it, never edit it.
 * @param {EventTarget} target
 * @param {string} identifier
 * @param {string} name
 * @param {Record<string, unknown>} [detail]
 * @param {{ cancelable?: boolean, bubbles?: boolean }} [options]
 * @returns {CustomEvent} check `.defaultPrevented` for cancelable events
 */
export function emit(target, identifier, name, detail = {}, { cancelable = false, bubbles = true } = {}) {
  const event = new CustomEvent(eventType(identifier, name), {
    detail: Object.freeze({ ...detail }),
    bubbles,
    cancelable,
  })
  target.dispatchEvent(event)
  return event
}

/**
 * Dispatch an app-wide `ls:<name>` event on window.
 * @param {string} name e.g. 'session-expired', 'toast', 'refresh'
 * @param {Record<string, unknown>} [detail]
 */
export function emitApp(name, detail = {}) {
  return emit(window, 'ls', name, detail)
}

const COMMAND_NAME = /^[a-z][a-z0-9-]*:[a-z][a-z0-9-]*$/

/**
 * Dispatch a command event on window whose name comes from markup or a command's detail (e.g. confirm's on-confirm event).
 * Unlike `emitApp` the name is not prefixed; it must be `component:event` (lower-case, digits, dashes), else TypeError.
 * @param {string} name e.g. 'template-editor:restore-confirmed'
 * @param {Record<string, unknown>} [detail] frozen copy
 */
export function emitCommand(name, detail = {}) {
  if (typeof name !== 'string' || !COMMAND_NAME.test(name)) throw new TypeError(`emitCommand: "${name}" is not a component:event name`)
  const event = new CustomEvent(name, { detail: Object.freeze({ ...detail }) })
  window.dispatchEvent(event)
  return event
}

/**
 * Arguments of a command that arrives either as a Stimulus action (`event.params`, from data-<id>-<name>-param
 * attributes) or as a CustomEvent on window (`event.detail`, e.g. `drawer:open`, `confirm:open`).
 * Stimulus always sets `params` (an empty object when there are none), so `params ?? detail` never reaches detail:
 * params win only when they have keys. Always a frozen copy.
 * @param {{ params?: Record<string, unknown>, detail?: Record<string, unknown> | null } | undefined} event
 * @returns {Readonly<Record<string, unknown>>}
 */
export function commandArgs(event) {
  const params = event?.params
  const source = params && Object.keys(params).length ? params : (event?.detail ?? {})
  return Object.freeze({ ...source })
}
